import { useMemo, useEffect } from 'react'
import { ClerkProvider, useUser, useAuth } from '@clerk/clerk-react'
import { setTokenGetter } from '@/lib/api'

// Inner component to set up token getter
function TokenGetterSetup({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth()

  useEffect(() => {
    // Set the token getter function for the API client
    setTokenGetter(async () => {
      try {
        return await getToken()
      } catch (error) {
        console.warn('[Clerk] Failed to get token:', error)
        return null
      }
    })
  }, [getToken])

  return <>{children}</>
}

export function ClerkWrapper({ children }: { children: React.ReactNode }) {
  // Get the base URL for redirects
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  return (
    <ClerkProvider
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      afterSignInUrl={baseUrl ? `${baseUrl}/` : '/'}
      afterSignUpUrl={baseUrl ? `${baseUrl}/` : '/'}
      signInUrl={baseUrl ? `${baseUrl}/sign-in` : '/sign-in'}
      signUpUrl={baseUrl ? `${baseUrl}/sign-up` : '/sign-up'}
    >
      <TokenGetterSetup>{children}</TokenGetterSetup>
    </ClerkProvider>
  )
}

export function useClerkAuth() {
  const { isSignedIn, isLoaded } = useAuth()
  const { user } = useUser()

  return useMemo(
    () => ({
      isAuthenticated: isSignedIn,
      user: user
        ? {
            id: user.id,
            username:
              user.username || user.primaryEmailAddress?.emailAddress || '',
            email: user.primaryEmailAddress?.emailAddress || '',
          }
        : null,
      isLoading: !isLoaded,
      login: () => {
        // Clerk handles login through components
        window.location.href = '/sign-in'
      },
      logout: () => {
        // Clerk handles logout through components
        window.location.href = '/sign-out'
      },
    }),
    [isSignedIn, isLoaded, user]
  )
}
